import React, {useState} from "react";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {InputTextarea} from "primereact/inputtextarea";
import Icon from "../../system/components/icon";
import {useTranslate} from "../../system/useTranslate";
import {useAi} from "../../models/ai/use-ai";

const ERROR_TRANSLATIONS = {
    "AI_NO_WORKSPACE": "ai-error-no-workspace",
    "ai.error.authentication_required": "ai-error-authentication",
    "ai.error.engine_unavailable": "ai-error-engine-unavailable",
    "ai.error.model_unavailable": "ai-error-model-unavailable",
    "ai.error.model_unpriced": "ai-error-model-unpriced",
    "ai.error.model_capability_mismatch": "ai-error-model-unavailable",
    "ai.error.disabled": "ai-error-disabled",
    "ai.error.rate_limited": "ai-error-rate-limited",
    "ai.error.daily_limit_reached": "ai-error-daily-limit",
    "ai.error.monthly_limit_reached": "ai-error-monthly-limit",
    "ai.error.concurrent_limit": "ai-error-rate-limited",
    "ai.error.request_too_large": "ai-error-too-large",
    "ai.error.invalid_request": "ai-error-invalid-request",
    "ai.error.provider_unavailable": "ai-error-provider-unavailable",
    "ai.error.provider_configuration": "ai-error-provider-configuration",
    "ai.error.credit_configuration": "ai-error-credit-configuration",
    "ai.error.out_of_scope": "ai-error-out-of-scope",
    "ai.error.forbidden": "ai-error-forbidden",
};

function AiPromptDialog({visible, setVisible, onComplete}) {
    const translate = useTranslate();
    const {sendMessage} = useAi();
    const [prompt, setPrompt] = useState("");
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);

    const close = () => {
        if (generating) return;
        setPrompt("");
        setError(null);
        setVisible(false);
    };

    const generate = async () => {
        const message = prompt.trim();
        if (!message) {
            setError(translate("ai-prompt-required"));
            return;
        }
        setGenerating(true);
        setError(null);
        try {
            const response = await sendMessage({message});
            const markdown = response?.data?.text || response?.text || "";
            if (!markdown) {
                setError(translate("ai-generate-empty"));
                return;
            }
            onComplete?.(markdown);
            setPrompt("");
            setVisible(false);
        } catch (generateError) {
            const code = generateError?.error?.details?.code
                || generateError?.details?.code
                || generateError?.code
                || null;
            const mapped = code && ERROR_TRANSLATIONS[code];
            const rawMessage = generateError?.error?.message
                || generateError?.details?.code
                || generateError?.message;
            console.error("[AiPromptDialog] generation failed", {code, rawMessage, generateError});
            setError(mapped
                ? translate(mapped)
                : (rawMessage && rawMessage !== "api-error" ? rawMessage : translate("ai-generate-failed")));
        } finally {
            setGenerating(false);
        }
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button
                type="button"
                label={translate("cancel")}
                className="p-button-text text-xs"
                disabled={generating}
                onClick={close}
            />
            <Button
                type="button"
                label={translate("ai-generate")}
                icon={<Icon icon="mi mi:auto_awesome" className="text-lg"/>}
                className="text-xs gap-2"
                loading={generating}
                disabled={generating}
                onClick={generate}
            />
        </div>
    );

    return (
        <Dialog
            className="w-4 rtl"
            dir="rtl"
            header={<span className="text-lg">{translate("ai-generate-prompt")}</span>}
            visible={visible}
            onHide={close}
            footer={footer}
        >
            <div className="flex flex-column gap-3">
                <InputTextarea
                    className="w-full text-xs"
                    rows={8}
                    autoFocus
                    placeholder={translate("ai-prompt-placeholder")}
                    value={prompt}
                    onChange={(event) => setPrompt(event.target.value)}
                />
                <div className="flex align-items-start gap-2 text-xs text-400">
                    <Icon icon="mi mi:info" className="text-lg text-400 flex-none"/>
                    <span>{translate("ai-prompt-hint")}</span>
                </div>
                {error && (
                    <div className="flex align-items-center gap-2 text-red-600 text-xs">
                        <Icon icon="mi mi:error" className="text-lg"/>
                        <span>{error}</span>
                    </div>
                )}
            </div>
        </Dialog>
    );
}

export default AiPromptDialog;
